import { InputAdornment } from '@mui/material';
import { Search } from 'lucide-react';
import {
  type KeyboardEvent,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import type { AutoCompleteOption } from 'src/components/Forms/types';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { useScrollPaginateHandler } from 'src/utils/hooks/use-scroll-paginate-handler';
import { MergeEntityCard } from './merge-entity-card';
import type { MergeTargetPickerProps } from './merge-target-picker.types';

enum MergeTargetPickerField {
  Target = 'target',
}

interface MergeTargetPickerFormValues {
  [MergeTargetPickerField.Target]: AutoCompleteOption | null;
}

const DEFAULT_VALUES: MergeTargetPickerFormValues = {
  [MergeTargetPickerField.Target]: null,
};

const PAGINATE_THRESHOLD_PX = 200;

type AutocompleteKeyboardEvent = KeyboardEvent<HTMLDivElement> & {
  defaultMuiPrevented?: boolean;
};

type PickedValue = AutoCompleteOption | string | (AutoCompleteOption | string)[] | null;

const getPickedId = (value: PickedValue) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value.id : undefined;

/**
 * Search for one entity until a target is picked, then the picked target as a card with a
 * remove button. The caller owns the search query and the picked record.
 */
export const MergeTargetPicker = <RecordT,>({
  label,
  placeholder,
  icon,
  records,
  target,
  toSummary,
  onTargetChange,
  onSearchChange,
  loading = false,
  hasMore = false,
  loadMore,
  searchInputRef,
  onArrowDownFromEmptySearch,
}: MergeTargetPickerProps<RecordT>) => {
  const formMethods = useForm<MergeTargetPickerFormValues>({ defaultValues: DEFAULT_VALUES });
  const [inputValue, setInputValue] = useState('');

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: PAGINATE_THRESHOLD_PX,
  });

  const options = useMemo<AutoCompleteOption[]>(
    () =>
      records.map((record) => {
        const { id, title } = toSummary(record);
        return { id, label: title, testId: 'merge-target-option' };
      }),
    [records, toSummary],
  );

  const handlePick = useCallback(
    (_event: SyntheticEvent, value: PickedValue) => {
      const pickedId = getPickedId(value);
      const record = records.find((candidate) => toSummary(candidate).id === pickedId);
      if (record) {
        onTargetChange(record);
      }
    },
    [records, toSummary, onTargetChange],
  );

  const handleInputChange = useCallback(
    (_event: SyntheticEvent, value: string, reason: string) => {
      if (reason === 'reset') {
        return;
      }
      setInputValue(value);
      onSearchChange(value);
    },
    [onSearchChange],
  );

  const handleKeyDown = useCallback(
    (event: AutocompleteKeyboardEvent) => {
      if (event.key === 'ArrowDown' && !inputValue && onArrowDownFromEmptySearch) {
        event.defaultMuiPrevented = true;
        event.preventDefault();
        onArrowDownFromEmptySearch();
      }
    },
    [inputValue, onArrowDownFromEmptySearch],
  );

  const handleRemove = useCallback(() => {
    formMethods.reset(DEFAULT_VALUES);
    setInputValue('');
    onSearchChange('');
    onTargetChange(null);
  }, [formMethods, onSearchChange, onTargetChange]);

  if (target) {
    return (
      <MergeEntityCard
        label={label}
        icon={icon}
        summary={toSummary(target)}
        onRemove={handleRemove}
        testId="merge-target-card"
      />
    );
  }

  return (
    <GenericForm<MergeTargetPickerFormValues> formMethods={formMethods}>
      <FormAutocomplete
        type="autocomplete"
        name={MergeTargetPickerField.Target}
        label={label}
        options={options}
        loading={loading}
        textfieldRef={searchInputRef}
        textFieldProps={{ placeholder }}
        inputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} />
            </InputAdornment>
          ),
        }}
        autocompleteProps={{
          autoHighlight: true,
          filterOptions: (unfiltered) => unfiltered,
          popupIcon: null,
          onChange: handlePick,
          onInputChange: handleInputChange,
          onKeyDown: handleKeyDown,
          ListboxProps: { onScroll: handleScroll },
        }}
        data-testid="merge-target-search"
      />
    </GenericForm>
  );
};
